import { Roles } from "../../../../constants/roles";
import { instagramEmbedForm } from "./instagram-embed-form";
import { InstagramEmbedListener } from "./instagram-embed-listener";

import { normalizeInstagramPermalinkForEmbed } from "../../../extractor/instagram-embed-extractor";

type InstagramEmbedOptions = {
    includeFallbackLink?: boolean;
};

function normalizeRenderedInstagramTileIframes(): void {
    instagramEmbedForm.getTileEmbedRoots(document).forEach((embedRoot) => {
        const iframe = instagramEmbedForm.getRenderedIframe(embedRoot);
        if (!(iframe instanceof HTMLIFrameElement)) {
            return;
        }

        const widthValue = globalThis
            .getComputedStyle(embedRoot)
            .getPropertyValue("--sns-embed-container-width-instagram")
            .trim();
        if (widthValue) {
            iframe.style.setProperty("width", widthValue, "important");
        }

        iframe.style.setProperty("max-width", "none", "important");
        iframe.style.setProperty("flex", "0 0 auto", "important");
    });
}

function scheduleInstagramTileIframeNormalization(): void {
    normalizeRenderedInstagramTileIframes();

    if (typeof globalThis.requestAnimationFrame === "function") {
        globalThis.requestAnimationFrame(() => normalizeRenderedInstagramTileIframes());
    }

    globalThis.setTimeout(() => normalizeRenderedInstagramTileIframes(), 150);
}

function buildInstagramEmbedNode(url: string, options: InstagramEmbedOptions = {}): HTMLElement | null {
    try {
        if (!url || typeof url !== "string") return null;

        const permalink = normalizeInstagramPermalinkForEmbed(url);
        if (!permalink) return null;

        const container = document.createElement("div");
        container.className = "sns-embed instagram-embed";
        container.setAttribute("data-role", Roles.SNS_EMBED);
        container.style.cssText = "margin-bottom: 16px; margin-top: 16px;";

        const blockquote = document.createElement("blockquote");
        blockquote.className = "instagram-media";
        blockquote.setAttribute("data-instgrm-captioned", "");
        blockquote.setAttribute("data-instgrm-permalink", permalink);
        blockquote.setAttribute("data-instgrm-version", "14");
        blockquote.style.cssText =
            "background:#fff; border:0; border-radius:8px; box-shadow:none; margin:0 auto; max-width:540px; min-width:0; padding:0; width:100%; overflow:hidden;";

        container.appendChild(blockquote);

        if (options.includeFallbackLink !== false) {
            const fallback = document.createElement("div");
            fallback.style.cssText = "margin-top: 8px;";
            const link = document.createElement("a");
            link.href = permalink;
            link.target = "_blank";
            link.rel = "noopener noreferrer";
            link.textContent = "View on Instagram";
            fallback.appendChild(link);
            container.appendChild(fallback);
        }

        return container;
    } catch (e) {
        console.error("buildInstagramEmbedNode failed", e);
        return null;
    }
}

export class InstagramEmbedRender {
    public static generateInstagramEmbed(url: string): HTMLElement | null {
        return buildInstagramEmbedNode(url);
    }

    public static generateInstagramEmbedForTile(url: string): HTMLElement | null {
        return buildInstagramEmbedNode(url, { includeFallbackLink: false });
    }

    public static ensureInstagramEmbedJsLoadedAndRefresh(): void {
        const refresh = (): void => {
            try {
                const instgrm = Reflect.get(globalThis, "instgrm");
                const embeds = instgrm && typeof instgrm === "object" ? Reflect.get(instgrm, "Embeds") : null;
                const process = embeds && typeof embeds === "object" ? Reflect.get(embeds, "process") : null;

                if (typeof process === "function") {
                    process.call(embeds);
                    scheduleInstagramTileIframeNormalization();
                }
            } catch {
                // ignore instagram embed refresh failures
            }
        };

        const existing = instagramEmbedForm.getEmbedScript(document);
        if (existing instanceof HTMLScriptElement) {
            if (existing.dataset.instagramEmbedReady === "1") {
                refresh();
                return;
            }

            InstagramEmbedListener.bindScriptLoad(existing, () => {
                existing.dataset.instagramEmbedReady = "1";
                refresh();
            });
            return;
        }

        const script = document.createElement("script");
        script.async = true;
        script.src = "https://www.instagram.com/embed.js";
        InstagramEmbedListener.bindScriptLoad(script, () => {
            script.dataset.instagramEmbedReady = "1";
            refresh();
        });
        document.head.appendChild(script);
    }
}

export { buildInstagramEmbedNode };
